// How full the model's context is, and with what: two hairline bars at the chat's top-left corner.
//   Sent  what the model actually got (after voitta-desktop dropped old screenshots), Anthropic's own count
//   Raw   what Claude Code built, before that: it can pass the model's window (the tick), even double it
// Both bars share one scale: the larger of the window and the raw total. Segments are what fills the context, in a
// fixed order and colour; hover or focus opens the numbers (also the legend). Server side: app/llmtap.py, context.py.
import { useState } from "react";

export type CtxCats = Partial<Record<"setup" | "images" | "results" | "calls" | "text" | "thinking" | "context", number>>;
export type Ctx = {
  conversation: string; brain: string; model?: string; window?: number | null;
  sent: CtxCats; raw: CtxCats; sent_total: number; raw_total: number;
  images?: { sent?: number; raw?: number }; exact?: boolean; measured_sent?: boolean; at?: number;
};

export const CATS: [keyof CtxCats, string][] = [
  ["setup", "System and tools"], ["images", "Images"], ["results", "Tool results"], ["calls", "Tool calls"],
  ["text", "Text"], ["thinking", "Thinking"], ["context", "Context"],
];

export function tok(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n));
}

function Bar({ cats, max, total, window }: { cats: CtxCats; max: number; total: number; window: number | null }) {
  const parts = CATS.filter(([k]) => (cats[k] ?? 0) > 0);
  return (
    <span className="ctx-bar">
      {window != null && <span className="ctx-track" style={{ width: `${(100 * window) / max}%` }} />}
      <span className="ctx-fill" style={{ width: `${(100 * total) / max}%` }}>
        {parts.map(([k]) => (
          <span key={k} className="ctx-seg" style={{ flexGrow: cats[k], background: `var(--ctx-${k})` }} />
        ))}
      </span>
    </span>
  );
}

// The drawer folds to the right, against the chat, leaving a handle. Remembered per browser; a private window or
// cleared storage just starts open again.
const FOLD_KEY = "vk.ctx.folded";
const readFolded = () => { try { return localStorage.getItem(FOLD_KEY) === "1"; } catch { return false; } };
const writeFolded = (v: boolean) => { try { localStorage.setItem(FOLD_KEY, v ? "1" : "0"); } catch { /* fine */ } };

export function ContextMeter({ ctx, style }: { ctx: Ctx; style?: React.CSSProperties }) {
  const [folded, setFolded] = useState(readFolded);
  const fold = (v: boolean) => { setFolded(v); writeFolded(v); };
  if (folded) return <ContextHandle ctx={ctx} style={style} onOpen={() => fold(false)} />;
  return <ContextDrawer ctx={ctx} style={style} onFold={() => fold(true)} />;
}

/** Folded: a slim tab against the chat's edge. Its two ticks keep the state visible: amber when the sent context
 *  is near the window, or the raw one past it. A click or Enter opens the drawer again. */
function ContextHandle({ ctx, style, onOpen }: { ctx: Ctx; style?: React.CSSProperties; onOpen: () => void }) {
  const win = ctx.window && ctx.window > 0 ? ctx.window : null;
  const tight = win != null && ctx.sent_total > 0.8 * win;
  const over = win != null && ctx.raw_total > win;
  return (
    <button className="ctx-handle" style={style} title={`Context: ${tok(ctx.sent_total)} sent${win ? ` of ${tok(win)}` : ""} · show`}
      aria-label="Show the context meter" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
      <i className={tight ? "warn" : ""} /><i className={over ? "warn" : ""} />
    </button>
  );
}

function ContextDrawer({ ctx, style, onFold }: { ctx: Ctx; style?: React.CSSProperties; onFold: () => void }) {
  const [open, setOpen] = useState(false);
  const win = ctx.window && ctx.window > 0 ? ctx.window : null;
  const max = Math.max(win ?? 0, ctx.raw_total, ctx.sent_total, 1);
  const tight = win != null && ctx.sent_total > 0.8 * win;
  const over = win != null && ctx.raw_total > win;
  const removed = Math.max(0, ctx.raw_total - ctx.sent_total);
  const rows = CATS.filter(([k]) => (ctx.raw[k] ?? 0) > 0 || (ctx.sent[k] ?? 0) > 0);
  const brain = ctx.brain === "codex" ? "Codex" : "Claude";
  const label = `Context: ${tok(ctx.sent_total)} sent${win ? ` of ${tok(win)}` : ""}, ${tok(ctx.raw_total)} before trimming`;

  return (
    <div className="ctx-meter" style={style} tabIndex={0} role="img" aria-label={label}
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onClick={(e) => e.stopPropagation()}>
      <span className="ctx-name">Sent</span>
      <Bar cats={ctx.sent} max={max} total={ctx.sent_total} window={win} />
      <span className={`ctx-val ${tight ? "warn" : ""}`}>{tok(ctx.sent_total)}{win ? <span className="muted"> / {tok(win)}</span> : null}</span>
      <span className="ctx-name">Raw</span>
      <Bar cats={ctx.raw} max={max} total={ctx.raw_total} window={win} />
      <span className={`ctx-val ${over ? "warn" : ""}`}>{tok(ctx.raw_total)}{over && win ? <span> · {(ctx.raw_total / win).toFixed(1)}×</span> : null}</span>
      {win != null && <span className="ctx-tick" style={{ left: `calc(var(--ctx-name-w) + (100% - var(--ctx-name-w) - var(--ctx-val-w) - var(--ctx-fold-w)) * ${win / max})` }} />}
      <button className="ctx-fold" title="Hide" aria-label="Hide the context meter"
        onClick={(e) => { e.stopPropagation(); onFold(); }} onFocus={(e) => e.stopPropagation()}>
        <svg viewBox="0 0 8 8" width="8" height="8" aria-hidden><path d="M2.5 1.5 5 4 2.5 6.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>

      {open && (
        <div className="ctx-pop" role="tooltip">
          <table>
            <thead><tr><th>{brain}{ctx.model ? <span className="muted"> · {ctx.model}</span> : null}</th><th>Sent</th><th>Raw</th></tr></thead>
            <tbody>
              {rows.map(([k, name]) => (
                <tr key={k}>
                  <td><i className="ctx-sw" style={{ background: `var(--ctx-${k})` }} />{name}</td>
                  <td>{tok(ctx.sent[k] ?? 0)}</td><td>{tok(ctx.raw[k] ?? 0)}</td>
                </tr>
              ))}
              <tr className="ctx-total"><td>Total</td><td>{tok(ctx.sent_total)}</td><td>{tok(ctx.raw_total)}</td></tr>
            </tbody>
          </table>
          <div className="ctx-notes">
            {win != null && <div>Window {tok(win)} · sent is {Math.round((100 * ctx.sent_total) / win)} % full</div>}
            {removed > 0 && <div>voitta-desktop removed {tok(removed)} ({Math.round((100 * removed) / ctx.raw_total)} %)
              {ctx.images?.raw ? ` · ${ctx.images.sent ?? 0} of ${ctx.images.raw} images sent` : ""}</div>}
            {ctx.brain === "codex" && <div>Codex reports a total only, not what fills it.</div>}
            {ctx.brain !== "codex" && ctx.measured_sent === false && removed === 0 &&
              <div>The request went straight to the model: nothing was trimmed.</div>}
            <div className="muted">Images as Anthropic counts them; the rest estimated from its size, scaled to the model's count.</div>
          </div>
        </div>
      )}
    </div>
  );
}
